// src/components/MetaTagsUpdater.js
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { SITE_URL, SITE_NAME, SITE_TAGLINE } from "../config/site";

/** Routes that should have noindex (app/dashboard/auth) */
const NOINDEX_PATHS = [
  "/companies",
  "/investor-tracker",
  "/investor-tracker/calendar",
  "/accelerator-tracker",
  "/dashboard",
  "/pitch-builder",
  "/settings",
  "/subscription",
  "/subscription-demo",
];

function isNoIndex(path) {
  return NOINDEX_PATHS.some((p) => path === p || path.startsWith(p + "/"));
}

// Define metadata for exact routes
const metaData = {
  "/": {
    title: `DeckLink.co | ${SITE_TAGLINE}`,
    description:
      "DeckLink is the exclusive platform smart founders use to impress investors, share their story, and raise capital—all with a single, powerful link. Investors rate DeckLink decks higher for clarity and professionalism.",
    ogTitle: `${SITE_NAME} – The Exclusive Fundraising Platform for Smart Founders`,
    ogDescription:
      "Impress investors and raise capital with a single, powerful link. DeckLink is trusted by top founders and rated highly by investors for clarity and professionalism.",
    keywords:
      "DeckLink, exclusive fundraising platform, pitch deck, startup funding, investor relations, smart founders, modern fundraising, investor experience, professional pitch, raise capital",
  },
  "/demo-preview": {
    title: "Stripe Pitch Deck Demo | Interactive Investor Presentation",
    description:
      "Experience Stripe's legendary pitch deck in an interactive format. See how the payment processing giant presented their vision, financials, and growth strategy to investors.",
    ogTitle: "Stripe Pitch Deck Demo – Interactive Investor Presentation",
    ogDescription:
      "Explore Stripe's iconic pitch deck in an interactive demo. Discover how they presented their payment processing vision, financials, and growth strategy to investors.",
    keywords:
      "Stripe pitch deck, payment processing, fintech, investor presentation, startup funding, payment technology, Stripe demo, interactive pitch deck",
  },
  "/blog": {
    title: `DeckLink Blog | Startup Funding Insights & Resources`,
    description:
      "Discover insights, guides, and resources for startup founders and investors. Get exclusive access to startup funding information and accelerator rankings.",
    ogTitle: `${SITE_NAME} Blog – Startup Funding Insights & Resources`,
    ogDescription:
      "Discover insights, guides, and resources for startup founders and investors. Get exclusive access to startup funding information and accelerator rankings.",
    keywords:
      "startup blog, funding insights, accelerator guides, startup resources, investor insights, DeckLink blog",
  },
  "/blog/top-100-accelerators": {
    title: "Top 100 Accelerators & Incubators 2025 | Latest Startup Funding Guide",
    description:
      "Discover the latest 2025 rankings of the top 100 accelerators and incubators for startups. Get exclusive insights on funding terms, acceptance rates, and success stories from Y Combinator, A16Z, Techstars, and more.",
    ogTitle: "Top 100 Accelerators & Incubators 2025 – Latest Startup Funding Guide",
    ogDescription:
      "Get the latest 2025 rankings of the best accelerators and incubators for your startup. Exclusive comprehensive guide with current funding terms, acceptance rates, and success stories from top programs worldwide.",
    keywords:
      "top accelerators 2025, latest startup incubators, Y Combinator 2025, A16Z 2025, Techstars 2025, startup funding 2025, latest accelerator programs, startup investment 2025, incubator programs 2025",
  },
  "/blog/how-to-pitch-ai-startup-investors-2025": {
    title: "How to Pitch Your AI Startup to Investors in 2025 | DeckLink Blog",
    description:
      "AI companies captured nearly half of global venture funding in 2025. Learn what VCs really want in AI pitches: defensibility, unit economics, AI literacy, and how to stand out when every deck has an AI slide.",
    ogTitle: "How to Pitch Your AI Startup to Investors in 2025 – What VCs Really Want",
    ogDescription:
      "Practical playbook for AI founders raising in 2025: defensibility, unit economics, pitch structure, and avoiding common mistakes. SEO-friendly guide for startup funding.",
    keywords:
      "pitch AI startup 2025, AI startup funding, VC pitch AI, AI investor pitch, startup fundraising 2025, AI startup investors, pitch deck AI",
  },
  "/blog/agentic-ai-openclaw-moltbot-fundraising-2026": {
    title: "Agentic AI (OpenClaw, Moltbot & Co) – What It Means for Fundraising in 2026 | DeckLink Blog",
    description:
      "Agentic AI and autonomous “AI operators” like OpenClaw and Moltbot are changing how founders build and run companies. Learn how to position your startup and data room when agents are part of your product and fundraising story.",
    ogTitle: "Agentic AI (OpenClaw, Moltbot & Co): Fundraising Implications in 2026",
    ogDescription:
      "How agentic AI tools and autonomous operators impact startup fundraising. Positioning, data room metrics, and narrative tips for founders building on the agent wave.",
    keywords:
      "agentic AI fundraising, AI operators fundraising, OpenClaw Moltbot, startup funding 2026, AI agents venture capital, autonomous agents startup",
  },
  "/blog/startup-fundraising-trends-2026": {
    title: "Startup Fundraising Trends in 2026: What Early-Stage Founders Need to Know | DeckLink Blog",
    description:
      "Venture funding is recovering but capital is concentrating into fewer, larger deals. Learn how seed and Series A rounds are changing in 2026, mega-rounds, diligence, and tactical moves for founders.",
    ogTitle: "Startup Fundraising Trends in 2026 – What Early-Stage Founders Need to Know",
    ogDescription:
      "Capital concentration, mega seed rounds, and evidence-based diligence in 2026. Practical playbook for early-stage founders raising pre-seed, seed, and Series A.",
    keywords:
      "startup fundraising 2026, seed round 2026, Series A 2026, VC trends 2026, founder fundraising, early-stage funding 2026, venture capital 2026",
  },
  "/blog/defense-tech-aerospace-funding-2025": {
    title: "Defense Tech and Aerospace Startup Funding 2025 | Record Year | DeckLink Blog",
    description:
      "Defense tech and aerospace startups had a record funding year in 2025. Where the capital is going: autonomous systems, dual-use tech, manufacturing scale. Trends and how to position.",
    ogTitle: "Defense Tech and Aerospace Startup Funding 2025 – Record Year and Where Capital Is Going",
    ogDescription:
      "Record defense tech and aerospace venture funding in 2025. Subsectors, geographic distribution, and how founders can position for this wave. Startup funding insights.",
    keywords:
      "defense tech funding 2025, aerospace startup funding, defense startup VC, defense tech venture capital 2025, aerospace investment 2025",
  },
  "/top-100-accelerators": {
    title: "Top 100 Accelerators & Incubators 2025 | Latest Startup Funding Guide",
    description:
      "Discover the latest 2025 rankings of the top 100 accelerators and incubators for startups. Get exclusive insights on funding terms, acceptance rates, and success stories from Y Combinator, A16Z, Techstars, and more.",
    ogTitle: "Top 100 Accelerators & Incubators 2025 – Latest Startup Funding Guide",
    ogDescription:
      "Get the latest 2025 rankings of the best accelerators and incubators for your startup. Exclusive comprehensive guide with current funding terms, acceptance rates, and success stories from top programs worldwide.",
    keywords:
      "top accelerators 2025, latest startup incubators, Y Combinator 2025, A16Z 2025, Techstars 2025, startup funding 2025, latest accelerator programs, startup investment 2025, incubator programs 2025",
  },
  "/privacy-policy": {
    title: "Privacy Policy | DeckLink",
    description: "DeckLink privacy policy. How we collect, use, and protect your information.",
    ogTitle: "Privacy Policy | DeckLink",
    ogDescription: "DeckLink privacy policy. How we collect, use, and protect your information.",
    keywords: "privacy policy, DeckLink, data protection",
  },
  "/signin": {
    title: "Sign In | DeckLink",
    description: "Sign in to your DeckLink account to manage your pitch decks and investor outreach.",
    ogTitle: "Sign In | DeckLink",
    ogDescription: "Sign in to your DeckLink account.",
    keywords: "sign in, login, DeckLink",
  },
  "/signup": {
    title: "Sign Up | DeckLink",
    description: "Create your DeckLink account and start impressing investors with a single, powerful link.",
    ogTitle: "Sign Up | DeckLink",
    ogDescription: "Create your DeckLink account and start impressing investors.",
    keywords: "sign up, register, DeckLink",
  },
  "/realestate": {
    title: "Real Estate Demo | DeckLink",
    description: "Interactive real estate pitch deck demo powered by DeckLink.",
    ogTitle: "Real Estate Demo | DeckLink",
    ogDescription: "Interactive real estate pitch deck demo powered by DeckLink.",
    keywords: "real estate, pitch deck, DeckLink demo",
  },
  "/onelinklanding": {
    title: "One Link Landing | DeckLink",
    description: "One link to share your pitch and capture investor interest.",
    ogTitle: "One Link Landing | DeckLink",
    ogDescription: "One link to share your pitch and capture investor interest.",
    keywords: "one link, landing, DeckLink",
  },
};

/** Get meta for path; supports pattern matches for dynamic routes */
function getMetaForPath(path) {
  const exact = metaData[path];
  if (exact) return { ...exact, canonical: `${SITE_URL}${path === "/" ? "" : path}` };

  // Dynamic: /blog/:id
  if (path.startsWith("/blog/")) {
    const slug = path.slice("/blog/".length);
    return {
      title: `Blog | ${SITE_NAME}`,
      description: "Startup funding insights and resources from DeckLink.",
      ogTitle: `Blog | ${SITE_NAME}`,
      ogDescription: "Startup funding insights and resources from DeckLink.",
      keywords: "blog, startup funding, DeckLink",
      canonical: `${SITE_URL}${path}`,
    };
  }
  // Public pitch preview: /p/:handle
  if (path.startsWith("/p/")) {
    return {
      title: `Pitch Deck | ${SITE_NAME}`,
      description: "View this pitch deck shared via DeckLink.",
      ogTitle: `Pitch Deck | ${SITE_NAME}`,
      ogDescription: "View this pitch deck shared via DeckLink.",
      keywords: "pitch deck, DeckLink, investor",
      canonical: `${SITE_URL}${path}`,
    };
  }

  // Default (home) with canonical for current path
  const fallback = metaData["/"];
  return {
    ...fallback,
    canonical: `${SITE_URL}${path === "/" ? "" : path}`,
  };
}

function MetaTagsUpdater() {
  const location = useLocation();

  useEffect(() => {
    const path = location.pathname;
    const currentMeta = getMetaForPath(path);
    const canonicalUrl = currentMeta.canonical || `${SITE_URL}${path === "/" ? "" : path}`;
    const noindex = isNoIndex(path);

    document.title = currentMeta.title;

    const setMeta = (selector, attr, value) => {
      const el = document.querySelector(selector);
      if (el && value) el.setAttribute(attr, value);
    };

    setMeta('meta[name="description"]', "content", currentMeta.description);
    setMeta('meta[name="keywords"]', "content", currentMeta.keywords);
    setMeta('meta[property="og:title"]', "content", currentMeta.ogTitle);
    setMeta('meta[property="og:description"]', "content", currentMeta.ogDescription);
    setMeta('meta[property="og:url"]', "content", canonicalUrl);
    setMeta('meta[name="twitter:title"]', "content", currentMeta.ogTitle);
    setMeta('meta[name="twitter:description"]', "content", currentMeta.ogDescription);

    // Canonical link
    let linkCanonical = document.querySelector('link[rel="canonical"]');
    if (!linkCanonical) {
      linkCanonical = document.createElement("link");
      linkCanonical.rel = "canonical";
      document.head.appendChild(linkCanonical);
    }
    linkCanonical.href = canonicalUrl;

    // Robots: noindex for app routes
    let metaRobots = document.querySelector('meta[name="robots"]');
    if (noindex) {
      if (!metaRobots) {
        metaRobots = document.createElement("meta");
        metaRobots.name = "robots";
        document.head.appendChild(metaRobots);
      }
      metaRobots.content = "noindex, nofollow";
    } else if (metaRobots) {
      metaRobots.content = "index, follow";
    }
  }, [location.pathname]);

  return null;
}

export default MetaTagsUpdater;
